import {
  type CaptionPresetsFormInput,
  type CaptionStyleFormInput,
} from '~/components/AssetModalV2/Content/Panel/Captions/captionPresetsFormValidationSchema';

export type {
  CaptionPresetsFormInput,
  CaptionStyleFormInput,
} from '~/components/AssetModalV2/Content/Panel/Captions/captionPresetsFormValidationSchema';

export type CaptionPosition = CaptionStyleFormInput['position'];

export type CaptionStyleId = CaptionPresetsFormInput['presets'][number]['id'];

/** A saved preset id, a successful no-op, or a failure that should stop a save batch. */
export type CaptionStyleSaveResult = CaptionStyleId | true | null;

export type CueLayout = CaptionStyleFormInput['cue'];

export type CaptionStyleFontFile = NonNullable<CaptionStyleFormInput['fontFile']>;

export type CaptionHoldMode = CaptionStyleFormInput['highlight']['holdMode'];

type OptionalCaptionHighlightTiming = 'holdGraceMs' | 'holdMode' | 'leadMs';

export type CaptionStyle = Omit<CaptionStyleFormInput, 'cue' | 'highlight'> & {
  cue?: CueLayout;
  highlight: Omit<CaptionStyleFormInput['highlight'], OptionalCaptionHighlightTiming> &
    Partial<Pick<CaptionStyleFormInput['highlight'], OptionalCaptionHighlightTiming>>;
};

export type CaptionStyleValues = CaptionStyleFormInput;

export type CaptionPresetFormRecord = CaptionPresetsFormInput['presets'][number];

export type CaptionPresetStylePath = `presets.${number}.style`;

export type CaptionPresetStyleFieldPath<Field extends string> = `${CaptionPresetStylePath}.${Field}`;

/** Server-side defaults from the tech spec. */
export const DEFAULT_CUE_LAYOUT: CueLayout = {
  maxWordsPerCue: 12,
  maxCharsPerLine: 42,
  maxLinesPerCue: 2,
  maxCueDurationMs: 7000,
  pauseBreakMs: 800,
};

export const DEFAULT_CAPTION_FONT = 'Inter';
export const DEFAULT_CAPTION_FONT_VARIANT = 'regular';

export const getCaptionStyleFormValues = (style: CaptionStyle): CaptionStyleFormInput => ({
  ...style,
  cue: { ...DEFAULT_CUE_LAYOUT, ...style.cue },
  ...(style.fontFile ? { fontFile: { ...style.fontFile } } : {}),
  highlight: {
    holdGraceMs: 0,
    holdMode: 'gapless',
    leadMs: 0,
    ...style.highlight,
  },
});

export const cloneCaptionStyleFormValues = (style: CaptionStyleFormInput): CaptionStyleFormInput => ({
  ...style,
  cue: { ...style.cue },
  ...(style.fontFile ? { fontFile: { ...style.fontFile } } : {}),
  highlight: { ...style.highlight },
});

export const getCaptionStyleValues = (style: CaptionStyleFormInput): CaptionStyleValues => ({
  color: style.color,
  cue: { ...style.cue },
  font: style.font,
  ...(style.fontFile ? { fontFile: { ...style.fontFile } } : {}),
  fontSizePx: style.fontSizePx,
  highlight: { ...style.highlight },
  position: style.position,
  shadow: style.shadow,
  variant: style.variant,
});
